import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environmentCAS';

export interface NeolianeV2ProfileRequest {
  zipCode: string;
  dateEffect: string;
  productType: string;  // "sante" ou "prevoyance"
  userApiKey?: string;
  profileHealth: NeolianeV2MemberHealth[];
}

export interface NeolianeV2MemberHealth {
  familyMember: string;  // "holder", "spouse", "child1", etc.
  birthYear: string;
  socialSecurityScheme: string;  // "1" ou "2"
}

export interface NeolianeV2ProfileResponse {
  id: string;
  status?: string;
  lead?: number;  // Lead ID pour la souscription
  leadPerson?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
}

export interface NeolianeV2Price {
  id: string;
  gammeId: number;
  gammeLabel: string;
  formulaId: number;
  formulaLabel: string;
  amount: number;
  members?: any[];
}

export interface NeolianeV2CartRequest {
  pricingId: string;
  members?: string[];
}

export interface NeolianeV2CartResponse {
  id: string;
  totalAmount: number;
  products: any[];
}

export interface NeolianeV2QuotationRequest {
  gender: string;
  lastname: string;
  firstname: string;
  address: string;
  email: string;
  b64?: boolean;
}

export interface NeolianeV2QuotationResponse {
  documentId: number;
  name: string;
  referer: number;
  file: string;
}

@Injectable({
  providedIn: 'root'
})
export class NeolianeV2Service {
  private baseUrl = `${environment.casApiUrl}${environment.apiEndpoint}/v1/neoliane/v2`;

  constructor(private http: HttpClient) {}

  /**
   * Créer un profil
   * POST /api/v1/neoliane/v2/profile
   */
  createProfile(request: NeolianeV2ProfileRequest): Observable<NeolianeV2ProfileResponse> {
    const url = `${this.baseUrl}/profile`;
    return this.http.post<NeolianeV2ProfileResponse>(url, request);
  }

  /**
   * Générer les prix
   * POST /api/v1/neoliane/v2/profile/{profileId}/generateprices
   */
  generatePrices(profileId: string): Observable<NeolianeV2Price[]> {
    const url = `${this.baseUrl}/profile/${profileId}/generateprices`;
    return this.http.post<NeolianeV2Price[]>(url, {});
  }

  /**
   * Créer un panier
   * POST /api/v1/neoliane/v2/profile/{profileId}/cart
   */
  createCart(profileId: string, products: NeolianeV2CartRequest[]): Observable<NeolianeV2CartResponse> {
    const url = `${this.baseUrl}/profile/${profileId}/cart`;
    return this.http.post<NeolianeV2CartResponse>(url, products);
  }

  /**
   * Générer un devis (retourne JSON avec base64)
   * POST /api/v1/neoliane/v2/profile/{profileId}/pricing/{pricingId}/quotation
   */
  generateQuotation(profileId: string, pricingId: string, request: NeolianeV2QuotationRequest): Observable<NeolianeV2QuotationResponse> {
    const url = `${this.baseUrl}/profile/${profileId}/pricing/${pricingId}/quotation`;
    return this.http.post<NeolianeV2QuotationResponse>(url, { ...request, b64: true });
  }

  /**
   * Télécharger un devis PDF
   * POST /api/v1/neoliane/v2/profile/{profileId}/pricing/{pricingId}/quotation/download
   */
  downloadQuotation(profileId: string, pricingId: string, request: NeolianeV2QuotationRequest): Observable<Blob> {
    const url = `${this.baseUrl}/profile/${profileId}/pricing/${pricingId}/quotation/download`;
    return this.http.post(url, request, {
      responseType: 'blob'
    });
  }

  /**
   * Workflow complet: Créer profil → Générer prix → Créer panier → Télécharger devis
   */
  async fullWorkflow(profileRequest: NeolianeV2ProfileRequest, quotationRequest: NeolianeV2QuotationRequest): Promise<{ prices: NeolianeV2Price[], pdfBlob: Blob, cart: NeolianeV2CartResponse }> {
    // 1. Créer le profil
    const profile = await this.createProfile(profileRequest).toPromise();
    if (!profile) throw new Error('Failed to create profile');

    // 2. Générer les prix
    const prices = await this.generatePrices(profile.id).toPromise();
    if (!prices || prices.length === 0) throw new Error('No prices available');

    // 3. Créer le panier avec le premier prix
    const firstPrice = prices[0];
    const cart = await this.createCart(profile.id, [{
      pricingId: firstPrice.id,
      members: ['holder']
    }]).toPromise();
    if (!cart) throw new Error('Failed to create cart');

    // 4. Télécharger le devis
    const pdfBlob = await this.downloadQuotation(profile.id, firstPrice.id, quotationRequest).toPromise();
    if (!pdfBlob) throw new Error('Failed to download quotation');

    return { prices, pdfBlob, cart };
  }
}
